import React from 'react';
import DOMPurify from 'dompurify';
import styled from 'styled-components';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import {
  CustomQuestion,
  CustomQuestionAnswer,
  CustomSingleQuestion,
} from 'admin/pages/ManageAccounts/CustomQuestions/utilts';

export const StyledCustomQuestionType = styled.div`
  max-width: 750px;
  margin: 0 auto ${SPACERS.small};
  border-radius: ${SPACERS.tiny};
  padding: ${SPACERS.medium};
  border: 1px solid ${COLORS.grayLight};
  .error-text {
    text-align: right;
    margin-top: ${SPACERS.small};
  }
  .save-button-row {
    display: flex;
    justify-content: flex-end;
    gap: ${SPACERS.tiny};
    margin-top: ${SPACERS.small};
  }
  button.save-button {
    min-width: 140px;
  }
  input,
  textarea {
    background-color: ${COLORS.whiteBase};
  }
`;

export interface ReportCustomQuestionTypePropsBase {
  disableEdit?: boolean;
  initialEdit?: boolean;
  saveButtonText?: string;
  reportId: number | string;
  answer: CustomQuestionAnswer[];
  handleAddDoneQuestionId: (questionId: number | string) => unknown;
  handleRemoveDoneQuestionId: (questionId: number | string) => unknown;
  onChange: (
    customQuestionIndex: number,
    questionIndex: number,
    questionId: number | string,
    answer: CustomQuestionAnswer,
  ) => unknown;
  customQuestionIndex: number;
  customQuestion: CustomQuestion;
  questionIndex: number;
  question: CustomSingleQuestion;
}

interface CustomQuestionSaveButtonProps {
  text?: string;
  className?: string;
  disabled: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  requestState: RequestState<any>;
  handleSubmit: () => unknown;
  handleCancel?: () => unknown;
}

export const CustomQuestionSaveButton: React.FC<CustomQuestionSaveButtonProps> = ({
  text = 'Save',
  disabled,
  requestState,
  className = 'save-button',
  handleSubmit,
  handleCancel,
}) => {
  const isSaving = requestState.type === 'REQUEST_START';
  return (
    <>
      {requestState.type === 'REQUEST_ERROR' && (
        <TrueImpactText color="critical" className="error-text">
          {requestState.error.message || 'Something went wrong while saving.'}
        </TrueImpactText>
      )}
      <div className="save-button-row">
        {handleCancel && (
          <TrueImpactButton
            variant="secondary-large"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </TrueImpactButton>
        )}
        <TrueImpactButton
          onClick={handleSubmit}
          className={className}
          isLoading={isSaving}
          disabled={disabled}
        >
          {text}
        </TrueImpactButton>
      </div>
    </>
  );
};

const StyledDiv = styled.div`
  a {
    text-decoration: none;
    color: ${COLORS.ctaPrimary};
    &:hover {
      text-decoration: underline;
    }
  }
`;

export const htmlToPlainText = (text: string | undefined): string => {
  if (!text) return '';
  const isHTML = text.includes('<') && text.includes('>');
  if (!isHTML) return text;
  const doc = new DOMParser().parseFromString(text, 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
};

export const RenderHtml: React.FC<{ text: string | undefined }> = ({
  text,
}) => {
  if (!text) return null;
  const isHTML = text.includes('<') && text.includes('>');
  return isHTML ? (
    <StyledDiv dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }} />
  ) : (
    <>{text}</>
  );
};
